
import { useState, useEffect } from 'react';

export default function Waitlist() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    vehicleMake: '',
    vehicleModel: '',
    vehicleYear: '',
    plateNumber: '',
    userType: 'both',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [captchaValue, setCaptchaValue] = useState('');
  const [captchaQuestion, setCaptchaQuestion] = useState({ num1: 0, num2: 0, answer: 0 });
  const [captchaError, setCaptchaError] = useState(false);

  useEffect(() => {
    generateCaptcha();
  }, []);

  const generateCaptcha = () => {
    const num1 = Math.floor(Math.random() * 10) + 1;
    const num2 = Math.floor(Math.random() * 10) + 1;
    setCaptchaQuestion({ num1, num2, answer: num1 + num2 });
    setCaptchaValue('');
    setCaptchaError(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (parseInt(captchaValue) !== captchaQuestion.answer) {
      setCaptchaError(true);
      generateCaptcha();
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');
    setCaptchaError(false);

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d69gkvtt1k61917cm7s0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          vehicleMake: '',
          vehicleModel: '',
          vehicleYear: '',
          plateNumber: '',
          userType: 'both',
        });
        generateCaptcha();
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <section id="waitlist" className="py-24 bg-teal-50">
      <div className="max-w-4xl mx-auto px-6">
        <div className="text-center mb-12">
          <div className="inline-flex items-center bg-teal-100 rounded-full px-4 py-2 mb-4">
            <i className="ri-user-add-line text-teal-600 mr-2 w-4 h-4 flex items-center justify-center"></i>
            <span className="text-xs font-medium text-teal-700" style={{ fontFamily: "'Inter', sans-serif" }}>
              Early Adopter Access
            </span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4" style={{ fontFamily: "'Montserrat', sans-serif" }}>
            Join the Waitlist
          </h2>
          <p className="text-base text-gray-600 max-w-2xl mx-auto" style={{ fontFamily: "'Inter', sans-serif" }}>
            Be among the first to experience smarter, safer, and more affordable mobility. Early adopters receive priority access and exclusive launch benefits.
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-sm p-8 md:p-10 border border-gray-100">
          {submitStatus === 'success' ? (
            <div className="text-center py-12">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-check-line text-4xl text-green-600 w-10 h-10 flex items-center justify-center"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                You're on the List!
              </h3>
              <p className="text-base text-gray-600 mb-6" style={{ fontFamily: "'Inter', sans-serif" }}>
                Thank you for joining. We'll notify you when the pilot program launches in Q3 2026.
              </p>
              <a
                href="https://wa.me/60123835600?text=Hi%2C%20I%20just%20joined%20the%20waitlist!"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center text-teal-600 font-medium hover:text-teal-700 cursor-pointer"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                <i className="ri-whatsapp-line mr-2 text-xl w-5 h-5 flex items-center justify-center"></i>
                Message us on WhatsApp for instant confirmation
              </a>
            </div>
          ) : (
            <form id="waitlist-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2" style={{ fontFamily: "'Inter', sans-serif" }}>
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-transparent text-sm"
                    style={{ fontFamily: "'Inter', sans-serif" }}
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2" style={{ fontFamily: "'Inter', sans-serif" }}>
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-transparent text-sm"
                    style={{ fontFamily: "'Inter', sans-serif" }}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2" style={{ fontFamily: "'Inter', sans-serif" }}>
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    placeholder="+60"
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-transparent text-sm"
                    style={{ fontFamily: "'Inter', sans-serif" }}
                  />
                </div>
                <div>
                  <label htmlFor="userType" className="block text-sm font-medium text-gray-700 mb-2" style={{ fontFamily: "'Inter', sans-serif" }}>
                    I want to *
                  </label>
                  <select
                    id="userType"
                    name="userType"
                    value={formData.userType}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-transparent text-sm"
                    style={{ fontFamily: "'Inter', sans-serif" }}
                  >
                    <option value="both">Drive & Ride</option>
                    <option value="driver">Drive Only</option>
                    <option value="passenger">Ride Only</option>
                  </select>
                </div>
              </div>

              <div className="border-t border-gray-100 pt-6">
                <h4 className="text-sm font-bold text-gray-900 mb-4" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                  Vehicle Details (for verification)
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="vehicleMake" className="block text-sm font-medium text-gray-700 mb-2" style={{ fontFamily: "'Inter', sans-serif" }}>
                      Vehicle Make
                    </label>
                    <input
                      type="text"
                      id="vehicleMake"
                      name="vehicleMake"
                      value={formData.vehicleMake}
                      onChange={handleChange}
                      placeholder="e.g., Toyota, Honda, Proton"
                      className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-transparent text-sm"
                      style={{ fontFamily: "'Inter', sans-serif" }}
                    />
                  </div>
                  <div>
                    <label htmlFor="vehicleModel" className="block text-sm font-medium text-gray-700 mb-2" style={{ fontFamily: "'Inter', sans-serif" }}>
                      Vehicle Model
                    </label>
                    <input
                      type="text"
                      id="vehicleModel"
                      name="vehicleModel"
                      value={formData.vehicleModel}
                      onChange={handleChange}
                      placeholder="e.g., Camry, Civic, X70"
                      className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-transparent text-sm"
                      style={{ fontFamily: "'Inter', sans-serif" }}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                  <div>
                    <label htmlFor="vehicleYear" className="block text-sm font-medium text-gray-700 mb-2" style={{ fontFamily: "'Inter', sans-serif" }}>
                      Year
                    </label>
                    <input
                      type="text"
                      id="vehicleYear"
                      name="vehicleYear"
                      value={formData.vehicleYear}
                      onChange={handleChange}
                      placeholder="e.g., 2022"
                      className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-transparent text-sm"
                      style={{ fontFamily: "'Inter', sans-serif" }}
                    />
                  </div>
                  <div>
                    <label htmlFor="plateNumber" className="block text-sm font-medium text-gray-700 mb-2" style={{ fontFamily: "'Inter', sans-serif" }}>
                      Plate Number
                    </label>
                    <input
                      type="text"
                      id="plateNumber"
                      name="plateNumber"
                      value={formData.plateNumber}
                      onChange={handleChange}
                      placeholder="e.g., WXY 1234"
                      className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-transparent text-sm"
                      style={{ fontFamily: "'Inter', sans-serif" }}
                    />
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-3" style={{ fontFamily: "'Inter', sans-serif" }}>
                  Vehicle details are optional but help expedite your verification for exclusive member benefits.
                </p>
              </div>

              <div className="border-t border-gray-100 pt-6">
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2 bg-gray-50 rounded-lg px-4 py-3">
                    <span className="text-sm font-medium text-gray-700" style={{ fontFamily: "'Inter', sans-serif" }}>
                      {captchaQuestion.num1} + {captchaQuestion.num2} =
                    </span>
                    <input
                      type="text"
                      value={captchaValue}
                      onChange={(e) => setCaptchaValue(e.target.value)}
                      placeholder="?"
                      required
                      className={`w-16 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-transparent text-sm text-center ${captchaError ? 'border-red-400 bg-red-50' : 'border-gray-200'}`}
                      style={{ fontFamily: "'Inter', sans-serif" }}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={generateCaptcha}
                    className="text-gray-500 hover:text-teal-600 transition-colors cursor-pointer"
                  >
                    <i className="ri-refresh-line text-xl w-5 h-5 flex items-center justify-center"></i>
                  </button>
                  {captchaError && (
                    <span className="text-xs text-red-500" style={{ fontFamily: "'Inter', sans-serif" }}>
                      Incorrect answer, please try again
                    </span>
                  )}
                </div>
              </div>

              {submitStatus === 'error' && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-sm text-red-800" style={{ fontFamily: "'Inter', sans-serif" }}>
                    Something went wrong. Please try again or contact us via WhatsApp.
                  </p>
                </div>
              )}

              <div className="flex flex-col sm:flex-row gap-4 pt-4">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 px-8 py-4 bg-teal-600 text-white rounded-full text-sm font-medium hover:bg-teal-700 transition-all shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  {isSubmitting ? 'Submitting...' : 'Join Waitlist'}
                </button>
                <a
                  href="https://wa.me/60123835600?text=Hi%2C%20I%27d%20like%20to%20join%20the%20waitlist"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 px-8 py-4 border-2 border-teal-600 text-teal-600 rounded-full text-sm font-medium hover:bg-teal-50 transition-all text-center whitespace-nowrap cursor-pointer"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  <i className="ri-whatsapp-line mr-2"></i>
                  Join via WhatsApp
                </a>
              </div>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
